import { calcClosedProfit } from '../../services/platform/TradingStore';

export const HISTORY_PAGE_SIZE = 100;

export const toNumber = value => {
  const num = parseFloat(value);
  return Number.isFinite(num) ? num : 0;
};

const firstNumber = (...values) => {
  for (const value of values) {
    const num = toNumber(value);
    if (num) return num;
  }

  return 0;
};

export const formatPrice = (value, digits = 5) => {
  const num = toNumber(value);
  return num ? num.toFixed(digits) : '0';
};

export const normalizeTimestamp = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  let timestamp = Number(value);
  if (!Number.isFinite(timestamp)) {
    timestamp = new Date(value).getTime();
  }
  if (!Number.isFinite(timestamp)) return null;
  if (timestamp < 100000000000) timestamp *= 1000;

  return timestamp;
};

export const formatDateTime = (value) => {
  const timestamp = normalizeTimestamp(value);
  if (!timestamp) return '-';

  return new Date(timestamp).toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'UTC',
  });
};

export const formatLocalTime = (value) => {
  const timestamp = normalizeTimestamp(value);
  if (!timestamp) return '';

  return new Date(timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
};

export const getTicketValue = (order) => (
  order?.deal ||
  order?.positionID ||
  order?.positionId ||
  order?.position ||
  order?.position_id ||
  order?.ticket ||
  order?.order ||
  '-'
);

export const getOrderSL = (order) => toNumber(order?.priceSL ?? order?.sl);
export const getOrderTP = (order) => toNumber(order?.priceTP ?? order?.tp);

export const getOrderTimestamp = (order) => (
  order?.timeCreateMsc ||
  order?.timeCreate ||
  order?.open_time ||
  order?.time
);

export const getOrderKey = (order) => (
  order?.deal ||
  order?.positionID ||
  order?.positionId ||
  order?.position ||
  order?.position_id ||
  order?.order ||
  order?.ticket ||
  order?.deal_id ||
  order?.id ||
  order?._id
);

export const hasExplicitProfit = (order) => {
  const value = order?.profit ?? order?.profitRaw ?? order?.profit_raw;
  return value !== undefined && value !== null && value !== '';
};

const getExplicitProfit = (order) => (
  toNumber(order?.profit ?? order?.profitRaw ?? order?.profit_raw)
);

export const getHistoryPositionId = (order) => (
  order?.positionID ||
  order?.positionId ||
  order?.positionByID ||
  order?.position_id ||
  order?.position_by_id ||
  order?.position ||
  order?.ticket ||
  order?.order
);

export const getHistoryDealTime = (order) => (
  normalizeTimestamp(order?.calculatedCloseTime) ||
  normalizeTimestamp(order?.closeTime) ||
  normalizeTimestamp(order?.close_time) ||
  normalizeTimestamp(order?.timeDoneMsc) ||
  normalizeTimestamp(order?.timeDone) ||
  normalizeTimestamp(order?.timeSetupMsc) ||
  normalizeTimestamp(order?.timeSetup) ||
  normalizeTimestamp(order?.timeMsc) ||
  normalizeTimestamp(order?.time_msc) ||
  normalizeTimestamp(order?.time) ||
  0
);

const getHistoryCloseTime = (order) => (
  normalizeTimestamp(order?.timeMsc) ||
  normalizeTimestamp(order?.time_msc) ||
  normalizeTimestamp(order?.time)
  // order?.closeTime ||
  // order?.close_time ||
  // order?.timeDoneMsc ||
  // (order?.timeDone ? order.timeDone * 1000 : null) ||
  // order?.timeSetupMsc ||
  // (order?.timeSetup ? order.timeSetup * 1000 : null) ||
  // order?.timeMsc ||
  // (order?.time ? order.time * 1000 : null)
);

export const getHistoryExecutionPrice = (order) => {
  const price = firstNumber(
    order?.priceClose,
    order?.price_close,
    order?.closePrice,
    order?.close_price,
    order?.PriceClose,
    order?.priceCurrent,
    order?.price_current,
    order?.currentPrice,
    order?.current_price,
    order?.PriceCurrent,
    order?.priceDone,
    order?.price_done,
    order?.donePrice,
    order?.done_price,
    order?.price
  );
  return price;
};

const getHistoryDirectOpenPrice = (order) => (
  firstNumber(
    order?.priceOpen,
    order?.price_open,
    order?.openPrice,
    order?.open_price,
    order?.OpenPrice,
    order?.priceOrder,
    order?.price_order,
    order?.orderPrice,
    order?.order_price,
    order?.PriceOrder,
    order?.pricePosition,
    order?.price_position,
    order?.positionPrice,
    order?.position_price,
    order?.price
  )
);

const getHistorySide = (order) => {
  const type = Number(order?.type ?? order?.order_type ?? order?.action);
  if (type === 0) return 'buy';
  if (type === 1) return 'sell';
  return String(order?.type ?? '').toLowerCase().includes('sell') ? 'sell' : 'buy';
};

const getHistoryEntry = (order) => {
  const rawEntry = order?.entry ?? order?.entryType ?? order?.entry_type;
  const entryNames = {
    IN: 0,
    OUT: 1,
    INOUT: 2,
    OUT_BY: 3,
  };
  const entry = typeof rawEntry === 'string' && Number.isNaN(Number(rawEntry))
    ? entryNames[rawEntry.toUpperCase()]
    : Number(rawEntry);
  return Number.isFinite(entry) ? entry : null;
};

const isMt5TradeDeal = (deal) => {
  const action = Number(deal?.action ?? deal?.type);
  return Boolean(deal?.symbol) &&
    Number(deal?.deal) > 0 &&
    Number(getHistoryPositionId(deal)) > 0 &&
    (action === 0 || action === 1);
};

const isVisibleClosedTradeDeal = (deal) => {
  const side = String(deal?.side ?? deal?.order_type ?? '').toUpperCase();
  return getHistoryEntry(deal) === 1 && Boolean(side) && side !== 'OTHER';
};

export const isBalanceHistoryDeal = (deal) => {
  const action = Number(deal?.action ?? deal?.type);
  return Number(deal?.deal) > 0 &&
    !deal?.symbol &&
    action !== 0 &&
    action !== 1;
};

export const getBalanceHistoryLabel = (deal) => {
  const profit = toNumber(deal?.profit ?? deal?.value);
  const comment = String(deal?.comment || '').toLowerCase();

  if (comment.includes('withdraw') || profit < 0) return 'Withdraw';
  if (comment.includes('deposit') || comment.includes('fund') || profit > 0) return 'Deposit';
  return 'Balance';
};

export const calculateHistoryProfitFromPrices = (order, openPrice, closePrice) => {
  return calcClosedProfit(order, openPrice, closePrice);
};

const findHistoryOpeningDeal = (order, history = []) => {
  const positionId = getHistoryPositionId(order);
  const currentTime = getHistoryDealTime(order);

  if (!positionId) return null;

  const currentSide = getHistorySide(order);
  return history
    .filter(item => item !== order)
    .filter(item => getHistoryPositionId(item) === positionId)
    .filter(item => getHistorySide(item) !== currentSide)
    .filter(item => getHistoryDealTime(item) <= currentTime || !currentTime)
    .sort((a, b) => getHistoryDealTime(b) - getHistoryDealTime(a))[0] || null;
};

export const getHistoryOpenClosePrices = (order, history = []) => {
  const closePrice = getHistoryExecutionPrice(order);
  const directOpenPrice = getHistoryDirectOpenPrice(order);
  const opener = directOpenPrice ? null : findHistoryOpeningDeal(order, history);
  const openPrice = directOpenPrice || (opener ? getHistoryExecutionPrice(opener) : 0);

  return { opener, openPrice, closePrice };
};

export const calculateHistoryProfit = (order, history = []) => {
  const { opener, openPrice, closePrice } = getHistoryOpenClosePrices(order, history);
  const hasPrices = Boolean(openPrice && closePrice);

  if (opener) {
    return calculateHistoryProfitFromPrices({
      ...opener,
      commission: toNumber(opener.commission) + toNumber(order.commission),
      swap: toNumber(opener.swap) + toNumber(order.swap),
      fee: toNumber(opener.fee) + toNumber(order.fee),
    }, openPrice, closePrice);
  }

  if (hasPrices) return calculateHistoryProfitFromPrices(order, openPrice, closePrice);
  if (hasExplicitProfit(order)) return getExplicitProfit(order);

  return 0;
};

const buildDealHistoryRows = (history = []) => {
  const tradeDeals = history.filter(isMt5TradeDeal);

  const grouped = new Map();

  tradeDeals.forEach(deal => {
    const positionId = getHistoryPositionId(deal);
    if (!grouped.has(positionId)) {
      grouped.set(positionId, {
        positionID: positionId,
        symbol: deal.symbol,
        openDeal: null,
        closeDeals: [],
      });
    }

    const entry = getHistoryEntry(deal);
    const group = grouped.get(positionId);

    if (entry === 0) group.openDeal = deal;
    if (entry === 1) group.closeDeals.push(deal);
  });

  const usedDeals = new Set();
  const rows = [];

  grouped.forEach(group => {
    const open = group.openDeal;
    const closes = group.closeDeals;
    if (closes.length === 0) return;

    const sortedCloses = [...closes].sort((a, b) => getHistoryDealTime(a) - getHistoryDealTime(b));

    if (!open) {
      sortedCloses.forEach(close => {
        const closeProfit = toNumber(close.profit);
        const commission = toNumber(close.commission);
        const storage = toNumber(close.storage ?? close.swap);
        const fee = toNumber(close.fee);

        rows.push({
          ...close,
          ticket: close.deal || close.ticket || close.order || group.positionID,
          positionID: group.positionID,
          position: group.positionID,
          symbol: close.symbol || group.symbol,
          calculatedClosePrice: getHistoryExecutionPrice(close),
          calculatedCloseTime: getHistoryCloseTime(close),
          calculatedProfit: closeProfit,
          netProfit: closeProfit + commission + storage + fee,
        });
        usedDeals.add(close);
      });
      return;
    }

    const lastClose = sortedCloses[sortedCloses.length - 1];
    const openPrice = getHistoryExecutionPrice(open) || getHistoryDirectOpenPrice(open);
    const closePrice = getHistoryExecutionPrice(lastClose);
    const closeProfit = sortedCloses.reduce((sum, deal) => sum + toNumber(deal.profit), 0);
    const allDeals = [open, ...sortedCloses];
    const commission = allDeals.reduce((sum, deal) => sum + toNumber(deal.commission), 0);
    const storage = allDeals.reduce((sum, deal) => sum + toNumber(deal.storage ?? deal.swap), 0);
    const fee = allDeals.reduce((sum, deal) => sum + toNumber(deal.fee), 0);
    const netProfit = closeProfit + commission + storage + fee;
    const hasDealProfit = sortedCloses.some(deal => hasExplicitProfit(deal));
    const calculatedProfit = hasDealProfit
      ? closeProfit
      : calculateHistoryProfitFromPrices({
        ...open,
        commission,
        swap: storage,
        fee,
      }, openPrice, closePrice);

    rows.push({
      ...lastClose,
      side: open.side,
      order_type: open.order_type ?? open.side,
      orderType: open.orderType ?? open.order_type ?? open.side,
      type: Number(open.action ?? open.type),
      action: Number(open.action ?? open.type),
      order: lastClose.order || group.positionID,
      ticket: lastClose.deal || lastClose.ticket || lastClose.order || group.positionID,
      positionID: group.positionID,
      position: group.positionID,
      symbol: group.symbol,
      volume: lastClose.volume,
      calculatedOpenPrice: openPrice,
      calculatedClosePrice: closePrice,
      calculatedCloseTime: getHistoryCloseTime(lastClose),
      profit: closeProfit,
      commission,
      storage,
      swap: storage,
      fee,
      netProfit,
      calculatedProfit,
    });

    allDeals.forEach(deal => usedDeals.add(deal));
  });

  return { rows, usedDeals };
};

const buildPairedHistoryRows = (history = []) => {
  const groups = new Map();

  history.forEach(order => {
    if (isMt5TradeDeal(order) || isBalanceHistoryDeal(order)) return;

    const positionId = getHistoryPositionId(order);
    if (!positionId) return;

    if (!groups.has(positionId)) groups.set(positionId, []);
    groups.get(positionId).push(order);
  });

  const pairedOrderIds = new Set();
  const pairedRows = [];

  groups.forEach(group => {
    if (group.length < 2) return;

    const sorted = [...group].sort((a, b) => getHistoryDealTime(a) - getHistoryDealTime(b));
    const opener = sorted[0];
    const closer = [...sorted].reverse().find(item => getHistorySide(item) !== getHistorySide(opener));

    if (!opener || !closer) return;

    const openPrice = getHistoryExecutionPrice(opener);
    const closePrice = getHistoryExecutionPrice(closer);
    if (!openPrice || !closePrice) return;

    const profitOrder = {
      ...opener,
      commission: toNumber(opener.commission) + toNumber(closer.commission),
      swap: toNumber(opener.swap) + toNumber(closer.swap),
      fee: toNumber(opener.fee) + toNumber(closer.fee),
    };

    pairedRows.push({
      ...opener,
      calculatedOpenPrice: openPrice,
      calculatedClosePrice: closePrice,
      calculatedCloseTime: getHistoryCloseTime(closer),
      calculatedProfit: calculateHistoryProfitFromPrices(profitOrder, openPrice, closePrice),
    });

    group.forEach(order => pairedOrderIds.add(order));
  });

  return { pairedRows, pairedOrderIds };
};

export const withCalculatedHistoryProfit = (history = []) => (
  (() => {
    const { rows: dealRows, usedDeals } = buildDealHistoryRows(history);
    const { pairedRows, pairedOrderIds } = buildPairedHistoryRows(history);
    const settledPositionIds = new Set(dealRows.map(row => String(getHistoryPositionId(row))).filter(Boolean));
    const balanceRows = history.filter(isBalanceHistoryDeal).map(deal => ({
      ...deal,
      isBalanceHistory: true,
      type: 'balance',
      ticket: deal.deal,
      order: deal.deal,
      calculatedProfit: toNumber(deal?.profit ?? deal?.value),
      netProfit: toNumber(deal?.profit ?? deal?.value),
    }));
    const singleRows = history.filter(order => (
      !usedDeals.has(order) &&
      !pairedOrderIds.has(order) &&
      Boolean(order?.symbol) &&
      !isMt5TradeDeal(order) &&
      !settledPositionIds.has(String(getHistoryPositionId(order)))
    )).map(order => {
    const { openPrice, closePrice } = getHistoryOpenClosePrices(order, history);

    return {
      ...order,
      calculatedOpenPrice: openPrice,
      calculatedClosePrice: closePrice,
      calculatedProfit: calculateHistoryProfit(order, history),
    };
    });

    return [...dealRows, ...pairedRows, ...balanceRows, ...singleRows]
      .filter(row => row.isBalanceHistory || isVisibleClosedTradeDeal(row))
      .sort((a, b) => getHistoryDealTime(b) - getHistoryDealTime(a));
  })()
);


// ====================================================================
//  Calculate history profit for a single order
// ===================================================================
export const calculateSingleHistoryProfit = (order) => {
  
};
